import React, { useState, useEffect, useMemo } from 'react';
import MedicalHistoryEditor from './MedicalHistoryEditor';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { fetchTemplate, fetchTemplates } from '../../../../services/surveys';
import { Translate } from 'react-localize-redux';
import { SmartFieldType } from '..';
import Modal from '../../../general/modal';
import { composeTemplatedMedicalHistoryValue } from './composeTemplatedValue';

export interface Template {
    uuid: string;
    name: string;
    content: string;
}

export enum TemplatedMedicalHistoryEditorType {
    SURGERY_REPORT = 1,
}

interface TemplatedMedicalHistoryEditorViewProps {
    type:TemplatedMedicalHistoryEditorType,
    uuidInvestigation: string,
    updateElement: (index: number, element: SmartFieldType) => void;
    onBlur?: () => void;
    initialValue?: string;
    initialTemplateId?: string;
    /**
     * SHER-244 — the live name of the parent Survey at the time the editor
     * is mounted. Embedded into the record's JSON value at save time so
     * the historical PDF/read view keeps the original title even if the
     * live template is renamed later. Optional for back-compat with
     * callers that haven't been updated yet (the read side falls back to
     * the live survey name).
     */
    surveyName?: string;
}
interface TemplatedMedicalHistoryEditorProps extends TemplatedMedicalHistoryEditorViewProps {
    fetchTemplates: (uuidInvestigation: string, type:TemplatedMedicalHistoryEditorType) => Promise<Template[]>;
    fetchTemplate: (uuidInvestigation: string, templateId: string) => Promise<Template | null>;
}

const TemplatedMedicalHistoryEditor: React.FC<TemplatedMedicalHistoryEditorProps> = ({
    type,
    uuidInvestigation,
    updateElement,
    onBlur,
    initialValue,
    initialTemplateId,
    surveyName
}) => {
    return <TemplatedMedicalHistoryEditorView type={type} uuidInvestigation={uuidInvestigation}
        updateElement={updateElement} onBlur={onBlur}
        fetchTemplates={fetchTemplates} fetchTemplate={fetchTemplate}
        initialValue={initialValue} initialTemplateId={initialTemplateId}
        surveyName={surveyName} />
}

export const TemplatedMedicalHistoryEditorView: React.FC<TemplatedMedicalHistoryEditorProps> = ({
    type,
    uuidInvestigation,
    fetchTemplates,
    fetchTemplate,
    updateElement,
    onBlur,
    initialValue,
    initialTemplateId,
    surveyName
}) => {
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loadingTemplates, setLoadingTemplates] = useState<boolean>(false);
    // selectedTemplateId is only set when the user explicitly picks a template
    // from the dropdown — never from hydrating a saved record.
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [templateContent, setTemplateContent] = useState<string | null>(initialValue ?? null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [pendingTemplateId, setPendingTemplateId] = useState<string>('');
    // displayTemplateId is purely cosmetic (which template name the dropdown shows);
    // it starts at the template the saved note came from.
    const [displayTemplateId, setDisplayTemplateId] = useState<string>(initialTemplateId || '');
    const [displayTemplateIdBeforeChange, setDisplayTemplateIdBeforeChange] = useState<string>('');

    useEffect(() => {
        const fetchTemplatesAsync = async () => {
            try {
                setLoadingTemplates(true);
                const templatesData = await fetchTemplates(uuidInvestigation, type)
                setTemplates(templatesData);
            } catch (error) {
                console.error('Failed to fetch templates:', error);
            }
            finally{
                setLoadingTemplates(false);
            }
        };

        fetchTemplatesAsync();
    }, []);

    // SHER-208: fetch a template's content ONLY when the user explicitly picks one
    // from the dropdown. An existing record's saved notes (initialValue) are the
    // source of truth — they must never be overwritten by the live template, which
    // may have been modified or deleted since the record was created.
    useEffect(() => {
        async function loadTemplate() {
            if (!selectedTemplateId) {
                return;
            }
            setIsLoading(true);
            try {
                const content = await fetchTemplate(uuidInvestigation, selectedTemplateId);
                // If the template was deleted or returned no content, leave the
                // editor (and the record) untouched.
                if (!content?.content) {
                    return;
                }
                setTemplateContent(content.content);
                setDisplayTemplateId(selectedTemplateId);
                updateElement(0, composeTemplatedMedicalHistoryValue({
                    templateId: selectedTemplateId,
                    notes: content.content,
                    surveyName,
                }));
            } catch (error) {
                console.error('Failed to load template:', error);
            } finally {
                setIsLoading(false);
            }
        }

        loadTemplate();
    }, [selectedTemplateId]);

    const handleTemplateChange = (event: SelectChangeEvent) => {
        const newTemplateId = event.target.value;

        // Applying a template overwrites the editor content. If there is already
        // content — an existing record, or text the user has typed — confirm first.
        const hasContent = !!(templateContent && templateContent.trim() !== ''
            && templateContent.trim() !== '<p><br></p>');

        if (hasContent) {
            // Show confirmation modal - store current display value before changing it
            setDisplayTemplateIdBeforeChange(displayTemplateId);
            setDisplayTemplateId(newTemplateId);
            setPendingTemplateId(newTemplateId);
            setShowConfirmModal(true);
        } else {
            // Empty editor — apply the template directly.
            setSelectedTemplateId(newTemplateId);
            setDisplayTemplateId(newTemplateId);
        }
    };

    const handleConfirmTemplateChange = async () => {
        setShowConfirmModal(false);
        setSelectedTemplateId(pendingTemplateId);
        setPendingTemplateId('');
    };

    const handleCancelTemplateChange = () => {
        setShowConfirmModal(false);
        // Revert display to what it was before the user tried to change it
        setDisplayTemplateId(displayTemplateIdBeforeChange);
        setPendingTemplateId('');
        setDisplayTemplateIdBeforeChange('');
    };

    const handleContentChange = (newContent: string) => {
        setTemplateContent(newContent);
        updateElement(0, composeTemplatedMedicalHistoryValue({
            // Preserve the template the note originated from when only the notes
            // are edited (the user hasn't picked a different template).
            templateId: selectedTemplateId || initialTemplateId || '',
            notes: newContent,
            surveyName,
        }));
    };

    // Memoize the initialValue to prevent new string references on every render
    const memoizedInitialValue = useMemo(() => {
        return templateContent || initialValue || '';
    }, [templateContent, initialValue]);

    // Create a key that changes when template actually changes, forcing remount
    const editorKey = useMemo(() => {
        // Key changes when:
        // 1. Template is selected/changed
        // 2. Initial value is set (first load)
        return selectedTemplateId || (initialValue ? 'initial' : 'empty');
    }, [selectedTemplateId, initialValue]);

    return (
        <div>
            <Modal
                open={showConfirmModal}
                closeModal={handleCancelTemplateChange}
                confirmAction={handleConfirmTemplateChange}
                title={<Translate id="hospital.template_medical_history_confirm_change_title" />}
            >
                <Typography variant="body2">
                    <Translate id="hospital.template_medical_history_confirm_change_message" />
                </Typography>
            </Modal>

            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="template-select-label"><Translate id="hospital.template_medical_history_select_template" /></InputLabel>
                <Select
                    labelId="template-select-label"
                    id="template-select"
                    value={displayTemplateId}
                    label={<Translate id="hospital.template_medical_history_select_template" />}
                    onChange={handleTemplateChange}
                    disabled={loadingTemplates}
                >
                    {templates?.map(template => (
                        <MenuItem key={template.uuid} value={template.uuid}>
                            {template.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {(selectedTemplateId || templateContent || initialValue) && (
                <MedicalHistoryEditor
                    key={editorKey}
                    initialValue={memoizedInitialValue}
                    loading={isLoading}
                    onChange={handleContentChange}
                    onBlur={onBlur}
                />
            )}
        </div>
    );
};

export default TemplatedMedicalHistoryEditor; 